import { FormGroup } from '@angular/forms';
import { Store } from '@ngrx/store';
import { debounceTime, filter, merge, mergeMap, of, startWith } from 'rxjs';
import { take, withLatestFrom } from 'rxjs/operators';
import { Revisione53State } from '../../../store/revisione53.reducers';
import { Revisione53CoreSelectors } from '../../../store/selectors/core.selectors';
import { Revisione53DatiBusinessSelectors } from '../../../store/selectors/dati-business.selectors';
import { getControl, getFormArray } from '@shared/helpers/form.helper';
import { withChangeDebounced } from '@shared/operators/with-change-debounced.operator';
import { CoopValidators } from '@core/helpers/validators.helper';
import { updateVerbaleOnStore } from '@core/helpers/create-validator.helper';
import { MyVerbale } from '../../../models/interfaces/revisione.interface';
import { Utilities } from '@core/helpers/utilities';
import { cloneDeep } from 'lodash-es';
import { getDatiIdentificativiControl, getDatiIdentificativiGroup } from '../validators/helpers/dati-identificativi-form-validators.helper';

export const setDatiIdentificativiIndirizziListeners = (form: FormGroup, store: Store<Revisione53State>): void => {
    my_CkIndirizzoPrincipale(form);
    my_ComuneSedeAmministrativa(form, store);
    my_ComuneSedeLegale(form, store);
    my_ComuneSedeRecapito(form, store);
    my_ProvinciaSedeAmministrativa(form, store);
    my_ProvinciaSedeLegale(form, store);
    my_ProvinciaSedeRecapito(form, store);
    my_TutteLeProvinceSelezionate(form, store);
};

const my_CkIndirizzoPrincipale = (form: FormGroup): void => {
    const datiIdentificativiGroup = getDatiIdentificativiGroup(form);
    const sedeAmministrativaFields = ['my_IndirizzoSedeAmministrativa', 'my_ProvinciaSedeAmministrativa', 'my_ComuneSedeAmministrativa', 'my_CapSedeAmministrativa'];
    const sedeRecapitoFields = ['my_IndirizzoSedeRecapito', 'my_ProvinciaSedeRecapito', 'my_ComuneSedeRecapito', 'my_CapSedeRecapito'];
    const my_CkIndirizzoPrincipaleControl = getControl(datiIdentificativiGroup, 'my_CkIndirizzoPrincipale');

    my_CkIndirizzoPrincipaleControl.valueChanges.pipe(withChangeDebounced(my_CkIndirizzoPrincipaleControl)).subscribe((ckIndirizzoPrincipale) => {
        [...sedeAmministrativaFields, ...sedeRecapitoFields].forEach((f) => {
            const ctrl = getControl(datiIdentificativiGroup, f);
            ctrl.clearValidators();
            ctrl.updateValueAndValidity();
        });

        if (ckIndirizzoPrincipale === '2') {
            sedeAmministrativaFields.forEach((field) => {
                const ctrl = getControl(datiIdentificativiGroup, field);
                ctrl.setValidators([CoopValidators.required("Compilare per intero l'indirizzo")]);
                ctrl.updateValueAndValidity();
            });
        }

        if (ckIndirizzoPrincipale === '3') {
            sedeRecapitoFields.forEach((field) => {
                const ctrl = getControl(datiIdentificativiGroup, field);
                ctrl.setValidators([CoopValidators.required("Compilare per intero l'indirizzo")]);
                ctrl.updateValueAndValidity();
            });
        }
    });
};

const my_ComuneSedeAmministrativa = (form: FormGroup, store: Store<Revisione53State>): void => {
    listenComuneToDesc(form, store, 'my_ComuneSedeAmministrativa', 'my_DescComuneSedeAmministrativa');
};

const my_ComuneSedeLegale = (form: FormGroup, store: Store<Revisione53State>): void => {
    listenComuneToDesc(form, store, 'my_ComuneSedeLegale', 'my_DescComuneSedeLegale');
};

const my_ComuneSedeRecapito = (form: FormGroup, store: Store<Revisione53State>): void => {
    listenComuneToDesc(form, store, 'my_ComuneSedeRecapito', 'my_DescComuneSedeRecapito');
};

const my_ProvinciaSedeAmministrativa = (form: FormGroup, store: Store<Revisione53State>): void => {
    listenProvinciaToDesc(form, store, 'my_ProvinciaSedeAmministrativa', 'my_DescProvinciaSedeAmministrativa', 'my_ComuneSedeAmministrativa', 'my_DescComuneSedeAmministrativa');
};

const my_ProvinciaSedeLegale = (form: FormGroup, store: Store<Revisione53State>): void => {
    listenProvinciaToDesc(form, store, 'my_ProvinciaSedeLegale', 'my_DescProvinciaSedeLegale', 'my_ComuneSedeLegale', 'my_DescComuneSedeLegale');
};

const my_ProvinciaSedeRecapito = (form: FormGroup, store: Store<Revisione53State>): void => {
    listenProvinciaToDesc(form, store, 'my_ProvinciaSedeRecapito', 'my_DescProvinciaSedeRecapito', 'my_ComuneSedeRecapito', 'my_DescComuneSedeRecapito');
};

const my_TutteLeProvinceSelezionate = (form: FormGroup, store: Store<Revisione53State>): void => {
    const datiId = getDatiIdentificativiGroup(form);
    const provinciaSedeLegaleCtrl = getControl(datiId, 'my_ProvinciaSedeLegale');
    const provinciaSedeAmministrativaCtrl = getControl(datiId, 'my_ProvinciaSedeAmministrativa');
    const provinciaSedeRecapitoCtrl = getControl(datiId, 'my_ProvinciaSedeRecapito');
    // TODO Sezione II fuori scope: reintegrare ProvinciaSedeLegaleII quando verrà migrata Sezione II.
    const allAmmUnFormArray = getFormArray(form, 'my_SezioneI__Rilevazione.my_AmministrazioneControllo.my_AllAmminUnico');
    const tutteLeProvinceCtrl = getControl(form, 'my_TutteLeProvinceSelezionate');

    merge(
        provinciaSedeLegaleCtrl.valueChanges.pipe(startWith(provinciaSedeLegaleCtrl.value)),
        provinciaSedeAmministrativaCtrl.valueChanges.pipe(startWith(provinciaSedeAmministrativaCtrl.value)),
        provinciaSedeRecapitoCtrl.valueChanges.pipe(startWith(provinciaSedeRecapitoCtrl.value)),
        allAmmUnFormArray.valueChanges.pipe(startWith(allAmmUnFormArray.value))
    )
        .pipe(debounceTime(Utilities.LISTENERS_DEBOUNCE_TIME))
        .subscribe(() => {
            const firstAmmUn = (allAmmUnFormArray.value as { my_AllAmmUnProvinciaCH: string }[])[0];
            const result = [provinciaSedeLegaleCtrl.value, provinciaSedeAmministrativaCtrl.value, provinciaSedeRecapitoCtrl.value, firstAmmUn?.my_AllAmmUnProvinciaCH ?? ''].join(
                ' , '
            );

            tutteLeProvinceCtrl.patchValue(result, { emitEvent: false });
            updateVerbaleOnStore(store, (myVerbale) => {
                const myVerbaleClone: MyVerbale = cloneDeep(myVerbale);
                myVerbaleClone.my_TutteLeProvinceSelezionate = result;
                return myVerbaleClone;
            });
        });
};

/** Listens to a comune field and populates the corresponding description field from the business data. */
const listenComuneToDesc = (form: FormGroup, store: Store<Revisione53State>, valueField: string, descField: string): void => {
    const datiId = getDatiIdentificativiGroup(form);
    const control = getControl(datiId, valueField);

    control.valueChanges
        .pipe(
            withChangeDebounced(control),
            mergeMap((value: string) => {
                if (!value) return of(null);
                return store.select(Revisione53DatiBusinessSelectors.getComune(value)).pipe(take(1));
            })
        )
        .subscribe((comune) => {
            const desc = comune?.Nome_comune ?? '';
            getDatiIdentificativiControl(form, descField)?.setValue(desc, { emitEvent: false });
            updateVerbaleOnStore(store, (myVerbale) => {
                const myVerbaleClone: MyVerbale = cloneDeep(myVerbale);
                myVerbaleClone.my_SezioneI__Rilevazione.my_DatiIdentificativi[descField] = desc;
                return myVerbaleClone;
            });
        });
};

/** Listens to a provincia field and populates description; on user change clears the comune fields. */
const listenProvinciaToDesc = (form: FormGroup, store: Store<Revisione53State>, valueField: string, descField: string, comuneField: string, comuneDescField: string): void => {
    const datiId = getDatiIdentificativiGroup(form);
    const control = getControl(datiId, valueField);

    control.valueChanges
        .pipe(
            withChangeDebounced(control),
            mergeMap((value: string) => {
                if (!value) return of(null);
                return store.select(Revisione53DatiBusinessSelectors.getProvincia(value)).pipe(take(1));
            })
        )
        .subscribe((provincia) => {
            const desc = provincia?.Nome ?? '';
            getDatiIdentificativiControl(form, descField)?.setValue(desc, { emitEvent: false });
            updateVerbaleOnStore(store, (myVerbale) => {
                const myVerbaleClone: MyVerbale = cloneDeep(myVerbale);
                myVerbaleClone.my_SezioneI__Rilevazione.my_DatiIdentificativi[descField] = desc;
                return myVerbaleClone;
            });
        });

    // withLatestFrom is placed BEFORE debounceTime so the store snapshot is taken at the moment
    // valueChanges fires, before formStoreValueStatusChanges (subscribed to form.valueChanges)
    // dispatches the new value to the store. This lets us distinguish a real user change
    // (emitted != store) from a populateForm(emitEvent:true) restore (emitted == store).
    control.valueChanges
        .pipe(
            withLatestFrom(store.select(Revisione53CoreSelectors.getMyVerbale)),
            debounceTime(Utilities.LISTENERS_DEBOUNCE_TIME),
            filter(([value, myVerbale]) => {
                const storeDatiId = myVerbale?.my_SezioneI__Rilevazione?.my_DatiIdentificativi;
                return value !== storeDatiId?.[valueField];
            })
        )
        .subscribe(() => {
            getDatiIdentificativiControl(form, comuneField)?.setValue('', { emitEvent: false });
            getDatiIdentificativiControl(form, comuneDescField)?.setValue('', { emitEvent: false });
            updateVerbaleOnStore(store, (myVerbale) => {
                const myVerbaleClone: MyVerbale = cloneDeep(myVerbale);
                myVerbaleClone.my_SezioneI__Rilevazione.my_DatiIdentificativi[comuneField] = '';
                myVerbaleClone.my_SezioneI__Rilevazione.my_DatiIdentificativi[comuneDescField] = '';
                return myVerbaleClone;
            });
        });
};
